import { z } from 'zod';

/**
 * Import der Zeiteinträge aus der Alt-App (Trockenlauf/Analyse).
 *
 * Schritt 1 des Imports: Die hochgeladene CSV wird geparst und gegen die
 * bestehenden ProERP-Stammdaten abgeglichen — es wird NICHTS geschrieben. Der
 * Report zeigt, was automatisch matcht (Kategorien, Tätigkeiten, Kostengruppen,
 * Firmen) und welche **Nutzer** und **Projekte** noch zugeordnet werden müssen.
 * Der eigentliche Import (Schritt 2) baut auf demselben Abgleich auf.
 */

/** Request: die rohe CSV als Text (Client liest die Datei und schickt den Inhalt). */
export const analyzeTimeEntryImportSchema = z.object({
  // Großzügiges Limit — ~5.000 Zeilen liegen deutlich unter 5 MB.
  csv: z.string().min(1).max(20_000_000),
});
export type AnalyzeTimeEntryImportInput = z.infer<typeof analyzeTimeEntryImportSchema>;

/** Eine der automatisch per Name abgeglichenen Dimensionen. */
export const importDimensionKeySchema = z.enum(['category', 'subCategory', 'costGroup', 'company']);
export type ImportDimensionKey = z.infer<typeof importDimensionKeySchema>;

/** Ein nicht abgleichbarer Dimensionswert samt Anzahl betroffener Zeilen. */
const unmatchedValueSchema = z.object({
  value: z.string(),
  rows: z.number().int(),
});

/** Coverage einer automatisch gemappten Dimension. */
export const importDimensionCoverageSchema = z.object({
  dimension: importDimensionKeySchema,
  /** Menschlich lesbares Label (z. B. „Kategorien"). */
  label: z.string(),
  /** Distinct Werte, die zu bestehenden Stammdaten passen. */
  matchedValues: z.number().int(),
  /** Distinct Werte ohne Treffer (mit Zeilen-Anzahl), leer = alles matcht. */
  unmatched: z.array(unmatchedValueSchema),
});
export type ImportDimensionCoverage = z.infer<typeof importDimensionCoverageSchema>;

/** Zuordnungs-Status eines Nutzers (E-Mail → Mitarbeiter). */
export const importUserMappingSchema = z.object({
  email: z.string(),
  rows: z.number().int(),
  hours: z.number(),
  status: z.enum(['matched', 'unmatched']),
  /** Gefundener Mitarbeiter (nur bei status='matched'). */
  employeeId: z.string().uuid().nullable(),
  employeeName: z.string().nullable(),
});
export type ImportUserMapping = z.infer<typeof importUserMappingSchema>;

/** Zuordnungs-Status einer Firma (CSV-Wert „AT"/„DE"/… → Gesellschaft/LegalEntity). */
export const importCompanyMappingSchema = z.object({
  value: z.string(),
  rows: z.number().int(),
  hours: z.number(),
  status: z.enum(['matched', 'unmatched']),
  /** Gefundene Gesellschaft (nur bei status='matched'). */
  legalEntityId: z.string().uuid().nullable(),
  legalEntityLabel: z.string().nullable(),
});
export type ImportCompanyMapping = z.infer<typeof importCompanyMappingSchema>;

/**
 * Zuordnungs-Status eines Projektnamens:
 * - `erp` = Name matcht ein bestehendes ProERP-Projekt (Kandidat für Ist-Kosten-Verknüpfung)
 * - `tt`  = Name matcht ein bestehendes Zeit-Projekt (TtProject)
 * - `new` = kein Treffer → würde beim Import als neues Zeit-Projekt angelegt
 */
export const importProjectMatchSchema = z.enum(['erp', 'tt', 'new']);
export type ImportProjectMatch = z.infer<typeof importProjectMatchSchema>;

export const importProjectMappingSchema = z.object({
  name: z.string(),
  rows: z.number().int(),
  hours: z.number(),
  match: importProjectMatchSchema,
  /** Ziel-ID (ProERP-Projekt bzw. Zeit-Projekt), null bei `new`. */
  targetId: z.string().uuid().nullable(),
  /** Anzeige-Label des Treffers (z. B. „P2610002 · Test mit Marc"), null bei `new`. */
  targetLabel: z.string().nullable(),
  /**
   * true, wenn der Name in Auftrags-/Vertriebs-/Neuentwicklungs-Kategorien auftaucht
   * — also ein Kandidat, den man an ein echtes ProERP-Projekt hängen möchte (statt
   * nur als Zeit-Projekt). Reine Overhead-Töpfe (Diverses, Pause …) sind false.
   */
  isOrderProject: z.boolean(),
});
export type ImportProjectMapping = z.infer<typeof importProjectMappingSchema>;

/** Zeilenweiser Parse-Hinweis (fehlerhafte/übersprungene Zeile). */
const importIssueSchema = z.object({
  line: z.number().int(),
  message: z.string(),
});

/**
 * Schritt 2 — Import ausführen. Der Client schickt dieselbe CSV plus die
 * Zuordnungen aus dem Trockenlauf. Der Server schreibt idempotent: gleiche
 * Zeile → gleicher deterministischer Schlüssel → beim erneuten Import
 * übersprungen (keine Dubletten).
 */

/** Entscheidung je Projektname (aus dem Trockenlauf-Bericht). */
export const importProjectDecisionSchema = z.object({
  /**
   * - `tt`   = bestehendes Zeit-Projekt verwenden (`targetId` erforderlich)
   * - `erp`  = an ein echtes ProERP-Projekt buchen (`targetId` erforderlich)
   * - `new`  = neues Zeit-Projekt mit dem Projektnamen anlegen
   * - `skip` = Zeilen dieses Projekts nicht importieren (z. B. „Pause")
   */
  mode: z.enum(['tt', 'erp', 'new', 'skip']),
  targetId: z.string().uuid().nullable().optional(),
});
export type ImportProjectDecision = z.infer<typeof importProjectDecisionSchema>;

export const importTimeEntriesSchema = z.object({
  csv: z.string().min(1).max(20_000_000),
  /** Login-E-Mail (kleingeschrieben) → Mitarbeiter-ID. Fehlt eine E-Mail, werden deren Zeilen übersprungen. */
  userMap: z.record(z.string(), z.string().uuid()),
  /** Projektname (wie in der CSV) → Zuordnungs-Entscheidung. */
  projectMap: z.record(z.string(), importProjectDecisionSchema),
  /** Firma (CSV-Wert, z. B. „AT"/„DE") → Gesellschaft-ID. Fehlt ein Wert, greift der automatische Abgleich. */
  companyMap: z.record(z.string(), z.string().uuid()).optional(),
});
export type ImportTimeEntriesInput = z.infer<typeof importTimeEntriesSchema>;

/** Ergebnis eines Imports. */
export const timeEntryImportResultSchema = z.object({
  /** Neu angelegte Zeiteinträge. */
  created: z.number().int(),
  /** Bereits vorhanden (idempotent übersprungen). */
  duplicates: z.number().int(),
  /** Zeilen ohne Import (kein Mitarbeiter, übersprungenes Projekt, Formatfehler). */
  skipped: z.number().int(),
  /** Dabei neu angelegte Zeit-Projekte. */
  createdTtProjects: z.number().int(),
  /** Gründe fürs Überspringen, gruppiert (mit Zeilenzahl). */
  skipReasons: z.array(z.object({ reason: z.string(), rows: z.number().int() })),
});
export type TimeEntryImportResult = z.infer<typeof timeEntryImportResultSchema>;

/** Vollständiger Analyse-Report des Trockenlaufs. */
export const timeEntryImportAnalysisSchema = z.object({
  totals: z.object({
    /** Erfolgreich geparste Datenzeilen (ohne Kopfzeile). */
    totalRows: z.number().int(),
    /** Zeilen, die wegen Formatfehlern übersprungen wurden. */
    skippedRows: z.number().int(),
    totalHours: z.number(),
    /** Frühestes/spätestes Datum (YYYY-MM-DD), null bei leerer Datei. */
    dateFrom: z.string().nullable(),
    dateTo: z.string().nullable(),
    /** Zeilen ohne Endzeit (in der Alt-App laufende Timer). */
    runningTimers: z.number().int(),
  }),
  dimensions: z.array(importDimensionCoverageSchema),
  users: z.array(importUserMappingSchema),
  companies: z.array(importCompanyMappingSchema),
  projects: z.array(importProjectMappingSchema),
  issues: z.array(importIssueSchema),
});
export type TimeEntryImportAnalysis = z.infer<typeof timeEntryImportAnalysisSchema>;
